/**
 * Book Detail — the guest list.
 *
 * One row per contributor: who they are, where they are in the journey, what
 * their chapter looks like, whether their email was opened / clicked / bounced,
 * how many reminders they have had, and the resend button.
 *
 * THE RESEND BUTTON IS THE ONLY THING HERE THAT WRITES. Its label changes with
 * the guest's status ("Send invitation" for someone never invited, "Resend"
 * otherwise) because the server branches on exactly that, and it is disabled —
 * with a tooltip that says why — when the ceiling of three reminders is reached
 * or the book is not accepting contributions. Those are the same two refusals
 * the server enforces; disabling here is courtesy, not security.
 *
 * No pagination: the product caps a book at 75 contributors (150 with extras).
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Typography,
  Tooltip,
  Button,
  Stack,
  CircularProgress,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/FileDownload';
import CircleIcon from '@mui/icons-material/Circle';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { GuestStatusChip } from '../WeddingChips';
import { num, relativeTime } from '../weddingFormat';
import type { BookStatus, EngagementTracking, GuestRow } from '../weddingApi';

interface Props {
  guests: GuestRow[];
  maxReminders: number;
  /** Server's verdict on whether opened_at/bounced_at have a writer here. */
  tracking?: EngagementTracking;
  bookStatus: BookStatus;
  loading?: boolean;
  /** Guest id currently being resent, so only that row spins. */
  resendingId?: string | null;
  onResend: (guest: GuestRow) => void;
  /**
   * Starts the guests CSV download. The URL is built by the page, which knows
   * the book id; this component stays presentation and never navigates itself.
   * Omitted ⇒ no button, so nothing renders a control that leads nowhere.
   */
  onExportCsv?: () => void;
}

/** Book states in which the wedding service will accept a send at all. */
const SENDABLE: BookStatus[] = ['active', 'collecting'];

/**
 * WB-13. Why one of these three dots says something different from the others.
 *
 * A grey dot whose tooltip reads "Opened: no" is a claim: it says this person
 * did not open their invitation, and we do not know that about anybody until
 * something writes `opened_at`. `tracked={false}` makes it say so instead.
 *
 * WHETHER IT IS TRACKED IS NOW A SERVER FACT, NOT A CONSTANT. The `wedding_stitch`
 * job reconciles provider events onto invitation rows every 15 minutes; the
 * guests endpoint reports whether it has ever succeeded, and these dots follow
 * that. Hardcoding `tracked={false}` here is what kept the whole reconciliation
 * invisible after the job shipped — the flag existed, was typed, and was read
 * by nothing.
 *
 * `clicked` IS measured whatever the stitch does — the /g/{code} gateway stamps
 * it — and an open is never inferred from it. See ADMIN_SECTION_DESIGN.md.
 */
const NOT_TRACKED_HELP =
  'not tracked — no per-guest open/bounce reconciliation has run against this database. '
  + 'Grey means no data, not "no".';

/** One engagement dot. Colour is a palette key so dark mode follows. */
const Dot: React.FC<{
  on: boolean;
  label: string;
  color: 'info' | 'success' | 'error';
  /** False when nothing writes the column this dot reads. */
  tracked?: boolean;
}> = ({ on, label, color, tracked = true }) => {
  // A true value is data whatever the column's reputation: if a webhook is ever
  // wired, this dot starts telling the truth without another edit here.
  const known = tracked || on;
  const state = known ? (on ? 'yes' : 'no') : NOT_TRACKED_HELP;

  return (
    <Tooltip title={`${label}: ${state}`}>
      <CircleIcon
        fontSize="inherit"
        sx={{
          fontSize: 10,
          color: on ? `${color}.main` : 'action.disabled',
          // An untracked dot is drawn hollow, so a row of them does not read as
          // three measurements of which two came back negative.
          ...(known ? {} : { opacity: 0.4 }),
        }}
        aria-label={`${label} ${known ? (on ? 'yes' : 'no') : 'not tracked'}`}
      />
    </Tooltip>
  );
};

const GuestsTable: React.FC<Props> = ({
  guests,
  maxReminders,
  tracking,
  bookStatus,
  loading = false,
  resendingId,
  onResend,
  onExportCsv,
}) => {
  const bookSendable = SENDABLE.includes(bookStatus);

  /**
   * `display: 'flex'` on every column — see WeddingBooksPage for the full
   * reason. Short version: x-data-grid v8 lays a cell out as `display: block`
   * unless told otherwise, and this grid's cells are all components. Without it
   * the two-line Chapter cell spills its "118 words · text" caption over the
   * row divider and onto the row below, which is what the first browser render
   * of this page showed (session I).
   */
  const columns: GridColDef<GuestRow>[] = [
    {
      field: 'name',
      headerName: 'Guest',
      flex: 1,
      minWidth: 200,
      display: 'flex',
      valueGetter: (_v, row) => `${row.first_name} ${row.last_name || ''}`.trim(),
      renderCell: (params: GridRenderCellParams<GuestRow>) => (
        <Box sx={{ overflow: 'hidden' }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
            {params.row.first_name} {params.row.last_name || ''}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {params.row.email}
            {params.row.relationship ? ` · ${params.row.relationship}` : ''}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<GuestRow>) => (
        <GuestStatusChip status={params.row.status} />
      ),
    },
    {
      field: 'chapter_status',
      headerName: 'Chapter',
      width: 190,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<GuestRow>) => {
        if (!params.row.chapter_id) {
          return (
            <Typography variant="body2" color="text.disabled">
              Not started
            </Typography>
          );
        }
        // A guest with more than one Chapter is a Platinum Contributor — that
        // second Chapter is the only way the count can exceed 1, so `> 1` IS
        // the tier gate. The status and word count beside it belong to the
        // FIRST Chapter alone (the query pins chapter_index = 0), so the chip
        // has to say what it is counting or it reads as a contradiction.
        const chapterCount = Number(params.row.chapter_count);
        return (
          <Box sx={{ overflow: 'hidden' }}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Typography variant="body2" noWrap>{params.row.chapter_status}</Typography>
              {chapterCount > 1 && (
                <Tooltip
                  title={`This guest has ${chapterCount} Chapters — a Platinum Contributor's second Chapter. The status and word count shown are the first Chapter's.`}
                >
                  <Chip size="small" variant="outlined" label={`×${chapterCount}`} />
                </Tooltip>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {params.row.word_count || 0} words
              {params.row.content_type ? ` · ${params.row.content_type}` : ''}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'engagement',
      headerName: 'Email',
      width: 130,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<GuestRow>) => (
        <Tooltip
          title={`${num(params.row.emails_sent)} invitations & reminders sent${
            params.row.last_sent_at ? `, last ${relativeTime(params.row.last_sent_at)}` : ''
          } — one-time codes and receipts are excluded`}
        >
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {num(params.row.emails_sent)}
            </Typography>
            {/* `?? false` and not `?? true`: a bundle talking to a backend that
                predates engagement_tracking must fall back to the honest
                answer, not to the confident one. */}
            <Dot
              on={params.row.ever_opened}
              label="Opened"
              color="info"
              tracked={tracking?.opened_tracked ?? false}
            />
            <Dot on={params.row.ever_clicked} label="Clicked" color="success" />
            <Dot
              on={params.row.ever_bounced}
              label="Bounced"
              color="error"
              tracked={tracking?.bounced_tracked ?? false}
            />
          </Stack>
        </Tooltip>
      ),
    },
    {
      field: 'reminder_count',
      headerName: 'Reminders',
      width: 110,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<GuestRow>) => (
        <Typography
          variant="body2"
          sx={{ color: params.row.reminder_count >= maxReminders ? 'warning.main' : 'text.primary' }}
        >
          {params.row.reminder_count} / {maxReminders}
        </Typography>
      ),
    },
    {
      /**
       * Q3's voice-clone consent record — migration 448.
       *
       * THIS COLUMN IS WHY THE EXPORT IS HONEST. The four voice_* fields were
       * projected by BOOK_GUESTS_SQL and written into the guests CSV while no
       * screen rendered them, so the spreadsheet carried columns the grid did
       * not — the exact inversion of the rule this table's export button
       * depends on, that the CSV is the grid.
       *
       * NULL IS A DASH, NEVER A "NO". A contributor who was never offered a
       * voice clone has not declined one, and rendering an absent consent as a
       * negative would misstate what somebody chose.
       */
      field: 'voice_status',
      headerName: 'Voice',
      width: 120,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<GuestRow>) => {
        const status = params.row.voice_status;
        if (!status) {
          return (
            <Tooltip title="No voice-clone consent on record — this guest was never offered one, which is not the same as declining.">
              <Typography variant="caption" color="text.secondary">—</Typography>
            </Tooltip>
          );
        }
        const seconds = params.row.voice_sample_seconds;
        const bits = [
          params.row.voice_consent_at
            ? `Consented ${relativeTime(params.row.voice_consent_at)}`
            : 'No consent timestamp recorded',
          params.row.voice_consent_text_version
            ? `consent text ${params.row.voice_consent_text_version}`
            : null,
          seconds ? `${num(seconds)}s of sample audio` : null,
        ].filter(Boolean);
        return (
          <Tooltip title={bits.join(' · ')}>
            <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
              {status.replace(/_/g, ' ')}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 170,
      sortable: false,
      filterable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<GuestRow>) => {
        const guest = params.row;
        const isFirstInvite = guest.status === 'pending';
        const atCeiling = !isFirstInvite && guest.reminder_count >= maxReminders;
        const submitted = guest.status === 'submitted';
        const busy = resendingId === guest.id;

        let reason = '';
        if (!bookSendable) reason = 'This book is not accepting contributions right now.';
        else if (submitted) reason = 'They have already written their story.';
        else if (atCeiling) reason = `They have had all ${maxReminders} reminders — that is as many as we send.`;

        const disabled = busy || !!reason;

        return (
          <Tooltip title={reason || (isFirstInvite ? 'Send their first invitation' : 'Send another reminder')}>
            {/* A disabled button swallows pointer events, so the tooltip needs a live wrapper. */}
            <span>
              <Button
                size="small"
                variant="outlined"
                disabled={disabled}
                startIcon={busy ? <CircularProgress size={14} /> : <SendIcon fontSize="small" />}
                onClick={(e) => {
                  e.stopPropagation();
                  onResend(guest);
                }}
              >
                {isFirstInvite ? 'Send invitation' : 'Resend'}
              </Button>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Card sx={{ borderRadius: '16px' }}>
      <CardContent>
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Guests
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {guests.length} contributor{guests.length === 1 ? '' : 's'} on this book.
              {bookSendable ? '' : ' Sending is unavailable while the book is in this state.'}
            </Typography>
          </Box>
          {onExportCsv && (
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={onExportCsv}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Export CSV
            </Button>
          )}
        </Stack>
        <Box sx={{ height: 520, width: '100%' }}>
          <DataGrid
            rows={guests}
            columns={columns}
            getRowId={(r) => r.id}
            loading={loading}
            disableRowSelectionOnClick
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            pageSizeOptions={[25, 50, 100]}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default GuestsTable;
